import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Mail, Lock, CheckCircle, User } from 'lucide-react';
import { toast } from 'sonner';

// Stryde Research Logo URLs
const STRYDE_ICON = "https://customer-assets.emergentagent.com/job_onboard-mastery/artifacts/x579wpam_stryde-fin-02.jpg";

const SetPassword = () => {
  const [step, setStep] = useState(1); // 1: enter email, 2: set password
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.checkUser(email);
      
      if (!response.data.must_set_password) {
        toast.error('Password already set. Please use regular login.');
        navigate('/login');
        return;
      }
      
      setUserName(response.data.name);
      setStep(2);
      toast.success(`Welcome, ${response.data.name}! Please set your password.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'User not found. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.setPassword(email, password);
      
      // Store token and user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Password set successfully! Welcome to StrydeMind.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="set-password-page">
      {/* Left Side - Stryde Branded */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden"
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          {/* Stryde Logo */}
          <div className="flex items-center gap-4 mb-10">
            <img 
              src={STRYDE_ICON} 
              alt="Stryde" 
              className="w-16 h-16 object-contain bg-white rounded-xl p-2"
            />
            <div>
              <h1 className="font-heading font-bold text-3xl">Stryde<span className="text-amber-400">Mind</span></h1>
              <p className="text-blue-200 text-sm">Learning Management System</p>
            </div>
          </div>
          
          <h2 className="text-4xl font-heading font-bold mb-4 leading-tight">
            First Time Login
          </h2>
          <p className="text-lg text-blue-100 leading-relaxed max-w-md mb-8">
            Your account has been created by an administrator. 
            Set your password to access your personalized learning dashboard.
          </p>

          {/* Steps */}
          <div className="space-y-4">
            <div className={`flex items-center gap-3 ${step >= 1 ? 'opacity-100' : 'opacity-50'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                step > 1 ? 'bg-emerald-500' : 'bg-white/10 backdrop-blur'
              }`}>
                {step > 1 ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-amber-400 font-bold">01</span>
                )}
              </div>
              <span className="text-blue-100">Verify your email address</span>
            </div>
            <div className={`flex items-center gap-3 ${step >= 2 ? 'opacity-100' : 'opacity-50'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                step > 2 ? 'bg-emerald-500' : 'bg-white/10 backdrop-blur'
              }`}>
                {step > 2 ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-amber-400 font-bold">02</span>
                )}
              </div>
              <span className="text-blue-100">Set your secure password</span>
            </div>
            <div className={`flex items-center gap-3 opacity-50`}>
              <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                <span className="text-amber-400 font-bold">03</span>
              </div>
              <span className="text-blue-100">Start your learning journey</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-card">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-4">
              <img 
                src={STRYDE_ICON} 
                alt="Stryde" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="font-heading font-bold text-xl text-blue-600">Stryde<span className="text-amber-500">Mind</span></h1>
              </div>
            </div>
            <CardTitle className="text-2xl font-heading font-bold text-slate-900">
              {step === 1 ? 'First Time Login' : `Welcome, ${userName}!`}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {step === 1 
                ? 'Enter the email your administrator used to create your account'
                : 'Create a secure password for your account'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleCheckEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
                  disabled={loading}
                  data-testid="check-email-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">{userName}</p>
                    <p className="text-xs text-blue-600">{email}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                      minLength={8}
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
                  disabled={loading}
                  data-testid="set-password-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Set Password & Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <Button 
                  type="button"
                  variant="ghost" 
                  className="w-full"
                  onClick={() => { setStep(1); setPassword(''); setConfirmPassword(''); }}
                >
                  Use different email
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Already set your password?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SetPassword;
